import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import StorageIcon from '@mui/icons-material/Storage';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import ListIcon from '@mui/icons-material/List';
import StarIcon from '@mui/icons-material/Star';
import GetAppIcon from '@mui/icons-material/GetApp';
import DescriptionIcon from '@mui/icons-material/Description';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import PageHeader from '../../components/PageHeader';
import SearchComponent from '../../components/Search/Search';
import { FLAG_URL } from '../../contants';
import {
  BuyAndBuildListDocument,
  useAddBuyAndBuildTargetsFromSourcescrubMutation,
  useGetCompanyForDfmLazyQuery,
  useGetSoftwareFirmsLazyQuery,
  GetSoftwareFirmsQuery,
  useMarkCompanyAsNotSoftwareMutation,
  useTransferBuyAndBuildTargetsToPipedriveMutation,
  useBuyAndBuildListLazyQuery,
} from '../../gql/generated/graphql';
import { getFirstNode } from '../../gql/helpers';
import { useCompanyNavigation } from '../../hooks/useCompanyNavigation';

import CompanyHeader from './../DFMPage/components/CompanyHeader/CompanyHeader';
import CustomPaper from './../DFMPage/components/CustomPaper/CustomPaper';
import ModernSection from './../DFMPage/components/ModernSection/ModernSection';
import { BBLonglist } from './BBLonglist';
import { BBShortlist } from './BBShortlist';
import { AddBuyAndBuildForm } from './components/AddBuyAndBuildForm';
import { BBCompanyOverview } from './components/BBCompanyOverview';
import { ImportFromExcelDialog } from './components/ImportFromExcelDialog';
import { exportBuyAndBuildToExcel } from './utils/exportToExcel';

import styles from './styles';

// Type alias for the actual query return type
type SoftwareFirmEdge = NonNullable<GetSoftwareFirmsQuery['softwareFirms']>['edges'][0];

export const MUI_RATING_SCALE = 5;

const BuyBuildPage = () => {
  const { t } = useTranslation();
  const { handleValidate, handleNavigateToAssesment } = useCompanyNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<SoftwareFirmEdge[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [value, setValue] = useState(0);
  const [company, setCompany] = useState<SoftwareFirmEdge['node'] | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [rateAndShortlistEnabled, setRateAndShortlistEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('bb.rateAndShortlist');
    return stored ? stored === '1' : false;
  });
  const [ratingFilter, setRatingFilter] = useState<'all' | 'manual' | 'automated'>('all');

  const companyParamsId = searchParams.get('companyId');

  const [getCompanyForDFM, { data: companyForDFM }] = useGetCompanyForDfmLazyQuery();

  const [markCompanyAsNotSoftware, { loading: markCompanyAsNotSoftwareLoading }] =
    useMarkCompanyAsNotSoftwareMutation();
  const [loadFromSourcescrub, { loading: loadFromSourcescrubLoading }] =
    useAddBuyAndBuildTargetsFromSourcescrubMutation();
  const [transferToPipedrive, { loading: transferToPipedriveLoading }] =
    useTransferBuyAndBuildTargetsToPipedriveMutation();

  useEffect(() => {
    let variables;
    if (companyParamsId) {
      variables = {
        variables: {
          companyId: companyParamsId,
        },
      };
    } else {
      if (!company) return;

      variables = {
        variables: {
          companyId: company?.dbId,
        },
      };
    }
    getCompanyForDFM(variables);
  }, [company]);

  const companyForDFMNode = getFirstNode(companyForDFM?.getCompany);
  const companyName =
    (companyForDFMNode?.shortName !== '<unnamed reference customer>' && companyForDFMNode?.shortName) ||
    companyForDFMNode?.legalName ||
    companyForDFMNode?.uniqueDomain;

  const companyAnalyses = companyForDFMNode?.companyAnalyses?.edges?.[0]?.node;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const debouncedSearch = useDebouncedCallback((value: string) => {
    search({ variables: { searchTerm: value } });
  }, 500);

  const [search, { loading }] = useGetSoftwareFirmsLazyQuery({
    onCompleted: data => {
      setOptions(data.softwareFirms.edges);
    },
  });

  const handleOptionSelect = (_, value: SoftwareFirmEdge | null) => {
    if (value === null) return;
    setCompany(value ? value.node : null);
    setSearchParams({ companyId: value?.node?.dbId?.toString() });
  };

  const handleInputChange = (_, value: string, reason: string) => {
    if (reason === 'clear') {
      handleReset();
      search();
      return;
    }

    setSearchTerm(value);
    if (value) {
      debouncedSearch(value);
    }
  };
  const handleReset = () => {
    setSearchTerm('');
    setOptions([]);
    setCompany(null);
  };

  const toggleRateAndShortlist = () => {
    const next = !rateAndShortlistEnabled;
    setRateAndShortlistEnabled(next);
    localStorage.setItem('bb.rateAndShortlist', next ? '1' : '0');
  };

  const handleMarkAsNotSoftware = (id: number) => {
    markCompanyAsNotSoftware({ variables: { companyId: id } });
  };

  const handleLoadFromSourcescrub = () => {
    if (!companyParamsId) return;

    loadFromSourcescrub({
      variables: {
        softwareCompanyId: parseInt(companyParamsId),
      },
      refetchQueries: [
        {
          query: BuyAndBuildListDocument,
          variables: { companyId: parseInt(companyParamsId), first: 10 },
        },
      ],
    });
  };

  const handleTransferToPipedrive = () => {
    if (!companyParamsId) return;

    const variables: { companyId: number; first?: number; isShortlisted?: boolean } = {
      companyId: parseInt(companyParamsId),
      first: 10,
    };

    if (value === 1) {
      variables.isShortlisted = true;
    }

    transferToPipedrive({
      variables: {
        softwareCompanyId: parseInt(companyParamsId),
      },
      refetchQueries: [
        {
          query: BuyAndBuildListDocument,
          variables,
        },
      ],
    });
  };

  const [getBuyAndBuildForExport] = useBuyAndBuildListLazyQuery();

  const fetchAllData = async (isShortlisted: boolean) => {
    if (!companyParamsId) return [];

    // Fetch all data in a single request without pagination
    const { data } = await getBuyAndBuildForExport({
      variables: {
        companyId: parseInt(companyParamsId),
        first: 100000, // Large number to get all results at once
        isShortlisted: isShortlisted || undefined,
      },
      fetchPolicy: 'network-only',
    });

    const edges = data?.companyAssesment?.edges?.[0]?.node?.buyAndBuildTargets?.edges || [];
    return edges.map(edge => ({
      ...edge?.node?.company,
      buyAndBuildTargetRelevance: edge?.node?.buyAndBuildRelevance,
      isShortlisted: edge?.node?.isShortlistedCompany1,
      manuallyLabeledScore: edge?.node?.manuallyLabeledScore,
    }));
  };

  const handleExportToExcel = async () => {
    if (!companyParamsId) return;

    setIsExporting(true);
    try {
      // Fetch all longlist data
      const allLonglistData = await fetchAllData(false);

      // Fetch all shortlist data
      const allShortlistData = await fetchAllData(true);

      // Export to Excel
      exportBuyAndBuildToExcel(allLonglistData, allShortlistData, companyName || 'Company');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const isCompany = companyParamsId && companyForDFMNode;

  return (
    <Box>
      <PageHeader
        variant={isCompany ? 'tabs' : 'search'}
        title={isCompany ? `Buy & Build: ${companyName}` : undefined}
        subtitle={isCompany ? `${companyForDFMNode?.legalName || ''} - ${companyForDFMNode?.city || ''}` : undefined}
        searchComponent={
          !isCompany ? (
            <SearchComponent
              options={options}
              loading={loading}
              searchTerm={searchTerm}
              onInputChange={handleInputChange}
              onOptionSelect={handleOptionSelect}
              onReset={handleReset}
            />
          ) : undefined
        }
        breadcrumbs={
          isCompany
            ? [
                { label: 'Home', path: '/' },
                { label: 'Buy & Build', path: '/buy-build' },
                { label: companyName || 'Company' },
              ]
            : undefined
        }
        tabs={
          isCompany
            ? [
                { label: t('buyAndBuild.longlistTab'), value: 0, icon: <ListIcon /> },
                { label: t('buyAndBuild.shortlistTab'), value: 1, icon: <StarIcon /> },
              ]
            : undefined
        }
        activeTab={isCompany ? value : undefined}
        onTabChange={isCompany ? handleChange : undefined}
        status={
          companyForDFMNode?.validated
            ? { label: t('assessment.validated'), color: 'success' }
            : companyForDFMNode
              ? { label: t('assessment.notValidated'), color: 'warning' }
              : undefined
        }
        actions={
          isCompany
            ? [
                {
                  label: 'From Excel',
                  icon: <CloudUploadIcon />,
                  onClick: () => setImportDialogOpen(true),
                  variant: 'outlined',
                },
                {
                  label: 'From Sourcescrub',
                  icon: <CloudUploadIcon />,
                  onClick: handleLoadFromSourcescrub,
                  variant: 'outlined',
                },
                {
                  label: 'To Pipedrive',
                  icon: <TransferWithinAStationIcon />,
                  onClick: handleTransferToPipedrive,
                  variant: 'contained',
                },
                {
                  label: isExporting ? 'Exporting...' : 'To Excel',
                  icon: <DescriptionIcon />,
                  onClick: handleExportToExcel,
                  variant: 'outlined',
                  disabled: isExporting,
                },
                // Right-side custom toggle for Rate & Shortlist
              ]
            : undefined
        }
        rightContent={
          isCompany ? (
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="rating-filter-label">Rating Type</InputLabel>
                <Select
                  labelId="rating-filter-label"
                  id="rating-filter"
                  value={ratingFilter}
                  label="Rating Type"
                  onChange={e => setRatingFilter(e.target.value as 'all' | 'manual' | 'automated')}
                >
                  <MenuItem value="all">All Ratings</MenuItem>
                  <MenuItem value="manual">Manual Only</MenuItem>
                  <MenuItem value="automated">Automated Only</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch size="small" checked={rateAndShortlistEnabled} onChange={toggleRateAndShortlist} />}
                label="Rate & Shortlist"
              />
            </Box>
          ) : undefined
        }
      />
      {isCompany && (
        <Box mt={1} gap={0} display="flex" flexDirection="column">
          {/* Company Overview */}
          <Box mt={1}>
            <BBCompanyOverview
              companyId={companyParamsId!}
              companyName={companyName}
              legalName={companyForDFMNode?.legalName}
              street={companyForDFMNode?.street}
              zipCode={companyForDFMNode?.zipCode}
              city={companyForDFMNode?.city}
              bbFocusDescription={companyForDFMNode?.buyAndBuildFocus}
            />
          </Box>

          {/* Data Integration Section */}
          <Box mt={1}>
            <AddBuyAndBuildForm companyId={companyParamsId} />
          </Box>

          {/* Targets Section */}
          <Box mt={1}>
            {value === 0 && (
              <BBLonglist
                companyId={companyParamsId}
                handleValidate={handleValidate}
                handleMarkAsNotSoftware={handleMarkAsNotSoftware}
                handleNavigateToAssesment={handleNavigateToAssesment}
                markCompanyAsNotSoftwareLoading={markCompanyAsNotSoftwareLoading}
                rateAndShortlistEnabled={rateAndShortlistEnabled}
                ratingFilter={ratingFilter}
              />
            )}
            {value === 1 && (
              <BBShortlist
                companyId={companyParamsId}
                handleValidate={handleValidate}
                handleMarkAsNotSoftware={handleMarkAsNotSoftware}
                handleNavigateToAssesment={handleNavigateToAssesment}
                markCompanyAsNotSoftwareLoading={markCompanyAsNotSoftwareLoading}
                rateAndShortlistEnabled={rateAndShortlistEnabled}
                ratingFilter={ratingFilter}
              />
            )}
          </Box>

          {/* Download Section */}
          <ModernSection
            title="ML Analysis"
            subtitle="Machine Learning generated Buy & Build list"
            icon={GetAppIcon}
            collapsible
            defaultExpanded={false}
          >
            <Box py={2}>
              <Button
                variant="contained"
                startIcon={<GetAppIcon />}
                href={`http://sourcing.bidequity.ai/crawler/search/get_buy_and_build_targets_for_company?company_id=${companyParamsId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download ML B&B List
              </Button>
            </Box>
          </ModernSection>
        </Box>
      )}
      {!isCompany && (
        <Box mt={2}>
          <Typography variant="h6" color="text.secondary">
            Please select a company to view Buy & Build targets
          </Typography>
        </Box>
      )}
      <CustomBackdrop open={loadFromSourcescrubLoading || transferToPipedriveLoading || isExporting} />
      {companyParamsId && (
        <ImportFromExcelDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          companyId={companyParamsId}
        />
      )}
    </Box>
  );
};

export default BuyBuildPage;
